import { createReducer, on } from '@ngrx/store';
import { Group } from '../../models/group.model';
import { HierarchyNode } from '../../models/hierarchy-node.model';
import { ManagementAssignment } from '../../models/management-assignment.model';
import { OrganizationMember } from '../../models/organization-member.model';
import { Organization } from '../../models/organization.model';
import * as ManagementActions from '../actions/management.actions';

export interface ManagementState {
  myAssignments: ManagementAssignment[];
  organizationAssignments: ManagementAssignment[];
  myOrganizations: Organization[];
  loadingMyOrganizations: boolean;
  currentOrganization: Organization | null;
  nodes: HierarchyNode[];
  selectedNodeId: string | null;
  selectedChurchId: string | null;
  affiliatedChurches: Group[];
  nodeLeaders: ManagementAssignment[];
  members: OrganizationMember[];
  loadingMembers: boolean;
  memberActionInProgress: boolean;
  churchOversight: {
    organization: Organization | null;
    lineage: HierarchyNode[];
    leaders: ManagementAssignment[];
  } | null;
  viewMode: 'list' | 'canvas';
  loading: boolean;
  error: any;
}

export const initialManagementState: ManagementState = {
  myAssignments: [],
  organizationAssignments: [],
  myOrganizations: [],
  loadingMyOrganizations: false,
  currentOrganization: null,
  nodes: [],
  selectedNodeId: null,
  selectedChurchId: null,
  affiliatedChurches: [],
  nodeLeaders: [],
  members: [],
  loadingMembers: false,
  memberActionInProgress: false,
  churchOversight: null,
  viewMode: 'canvas',
  loading: false,
  error: null,
};

export const managementReducer = createReducer(
  initialManagementState,

  // My Assignments
  on(ManagementActions.loadMyAssignments, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ManagementActions.loadMyAssignmentsSuccess, (state, { assignments }) => ({
    ...state,
    myAssignments: assignments,
    loading: false,
  })),
  on(ManagementActions.loadMyAssignmentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Organization Assignments
  on(ManagementActions.loadOrganizationAssignmentsSuccess, (state, { assignments }) => ({
    ...state,
    organizationAssignments: assignments,
  })),

  // Organization
  on(ManagementActions.loadOrganization, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ManagementActions.loadOrganizationSuccess, (state, { organization }) => ({
    ...state,
    currentOrganization: organization,
    loading: false,
  })),
  on(ManagementActions.loadOrganizationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Hierarchy Nodes
  on(ManagementActions.loadHierarchyNodes, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ManagementActions.loadHierarchyNodesSuccess, (state, { nodes }) => ({
    ...state,
    nodes,
    selectedNodeId: nodes.some((n) => n.id === state.selectedNodeId)
      ? state.selectedNodeId
      : nodes.length > 0
      ? nodes[0].id
      : null,
    loading: false,
  })),
  on(ManagementActions.loadHierarchyNodesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Affiliated Churches
  on(ManagementActions.loadAffiliatedChurches, (state) => ({
    ...state,
    loading: true,
  })),
  on(ManagementActions.loadAffiliatedChurchesSuccess, (state, { churches }) => ({
    ...state,
    affiliatedChurches: churches,
    loading: false,
  })),
  on(ManagementActions.loadAffiliatedChurchesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Node Leaders
  on(ManagementActions.loadNodeLeaders, (state) => ({
    ...state,
  })),
  on(ManagementActions.loadNodeLeadersSuccess, (state, { leaders }) => ({
    ...state,
    nodeLeaders: leaders,
  })),
  on(ManagementActions.loadNodeLeadersFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Church Oversight
  on(ManagementActions.loadChurchOversight, (state) => ({
    ...state,
    loading: true,
  })),
  on(ManagementActions.loadChurchOversightSuccess, (state, oversight) => ({
    ...state,
    churchOversight: oversight,
    loading: false,
  })),
  on(ManagementActions.loadChurchOversightFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // UI State
  on(ManagementActions.selectHierarchyNode, (state, { nodeId }) => ({
    ...state,
    selectedNodeId: nodeId,
    selectedChurchId: null,
  })),
  on(ManagementActions.selectChurch, (state, { churchId }) => {
    let parentNodeId = state.selectedNodeId;
    if (churchId) {
      const church = state.affiliatedChurches.find((c) => c.id === churchId);
      if (church && church.parentNodeId) {
        parentNodeId = church.parentNodeId;
      }
    }
    return {
      ...state,
      selectedChurchId: churchId,
      selectedNodeId: parentNodeId,
    };
  }),
  on(ManagementActions.createHierarchyNodeSuccess, (state, { nodeId }) => ({
    ...state,
    selectedNodeId: nodeId,
    selectedChurchId: null,
  })),
  on(ManagementActions.deleteHierarchyNodeSuccess, (state, { nodeId, parentNodeId }) => ({
    ...state,
    selectedNodeId: state.selectedNodeId === nodeId ? parentNodeId : state.selectedNodeId,
    selectedChurchId: null,
  })),
  on(ManagementActions.setViewMode, (state, { viewMode }) => ({
    ...state,
    viewMode,
  })),

  // My Organizations
  on(ManagementActions.loadMyOrganizations, (state) => ({
    ...state,
    loadingMyOrganizations: true,
  })),
  on(ManagementActions.loadMyOrganizationsSuccess, (state, { organizations }) => ({
    ...state,
    myOrganizations: organizations,
    loadingMyOrganizations: false,
  })),
  on(ManagementActions.loadMyOrganizationsFailure, (state, { error }) => ({
    ...state,
    loadingMyOrganizations: false,
    error,
  })),

  // Organization Members
  on(ManagementActions.loadOrganizationMembers, (state) => ({
    ...state,
    loadingMembers: true,
  })),
  on(ManagementActions.loadOrganizationMembersSuccess, (state, { members }) => ({
    ...state,
    members,
    loadingMembers: false,
  })),
  on(ManagementActions.loadOrganizationMembersFailure, (state, { error }) => ({
    ...state,
    loadingMembers: false,
    error,
  })),

  // Invite Member
  on(ManagementActions.inviteOrganizationMember, (state) => ({
    ...state,
    memberActionInProgress: true,
  })),
  on(ManagementActions.inviteOrganizationMemberSuccess, (state, { member }) => ({
    ...state,
    members: [member, ...state.members.filter((m) => m.id !== member.id)],
    memberActionInProgress: false,
  })),
  on(ManagementActions.inviteOrganizationMemberFailure, (state, { error }) => ({
    ...state,
    memberActionInProgress: false,
    error,
  })),

  // Request Membership
  on(ManagementActions.requestOrganizationMembership, (state) => ({
    ...state,
    memberActionInProgress: true,
  })),
  on(ManagementActions.requestOrganizationMembershipSuccess, (state, { member }) => ({
    ...state,
    members: [member, ...state.members.filter((m) => m.id !== member.id)],
    memberActionInProgress: false,
  })),
  on(ManagementActions.requestOrganizationMembershipFailure, (state, { error }) => ({
    ...state,
    memberActionInProgress: false,
    error,
  })),

  // Update Member Status
  on(ManagementActions.updateOrganizationMemberStatus, (state) => ({
    ...state,
    memberActionInProgress: true,
  })),
  on(ManagementActions.updateOrganizationMemberStatusSuccess, (state, { memberDocId, status, role }) => ({
    ...state,
    members: state.members.map((m) =>
      m.id === memberDocId
        ? {
            ...m,
            status,
            role: role || m.role,
          }
        : m
    ),
    memberActionInProgress: false,
  })),
  on(ManagementActions.updateOrganizationMemberStatusFailure, (state, { error }) => ({
    ...state,
    memberActionInProgress: false,
    error,
  })),

  // Update Organization
  on(ManagementActions.updateOrganization, (state) => ({
    ...state,
    memberActionInProgress: true,
  })),
  on(ManagementActions.updateOrganizationSuccess, (state, { orgId, updates }) => ({
    ...state,
    currentOrganization:
      state.currentOrganization && state.currentOrganization.id === orgId
        ? { ...state.currentOrganization, ...updates }
        : state.currentOrganization,
    myOrganizations: state.myOrganizations.map((org) =>
      org.id === orgId ? { ...org, ...updates } : org
    ),
    memberActionInProgress: false,
  })),
  on(ManagementActions.updateOrganizationFailure, (state, { error }) => ({
    ...state,
    memberActionInProgress: false,
    error,
  })),

  // Move Church Affiliation
  on(ManagementActions.moveChurchAffiliation, (state) => ({
    ...state,
    loading: true,
  })),
  on(ManagementActions.moveChurchAffiliationSuccess, (state, { churchId, targetNodeId }) => ({
    ...state,
    affiliatedChurches: state.affiliatedChurches.map((c) =>
      c.id === churchId ? { ...c, parentNodeId: targetNodeId } : c
    ),
    loading: false,
  })),
  on(ManagementActions.moveChurchAffiliationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  }))
);
